
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { RiskAssessmentQuestions } from './operational/RiskAssessmentQuestions';
import { BusinessOperations } from './operational/BusinessOperations';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import type { Application } from '@/types/database.types';

interface OperationalDetailsProps {
  data: Partial<Application>;
  onSave: (data: Partial<Application>) => void;
}

export const OperationalDetails = ({ data, onSave }: OperationalDetailsProps) => {
  const { toast } = useToast();
  
  const { register, handleSubmit, reset, setValue, watch } = useForm<Partial<Application>>({
    defaultValues: {
      // Basic operational details
      business_activities: data.business_activities || '',
      has_over_road_trucking: data.has_over_road_trucking || false,
      has_cannabis_hemp_contact: data.has_cannabis_hemp_contact || false,
      cannabis_hemp_details: data.cannabis_hemp_details || '',
      has_elevated_workplace_risk: data.has_elevated_workplace_risk || false,
      workplace_risk_details: data.workplace_risk_details || '',
      has_company_aircraft: data.has_company_aircraft || false,
      has_marine_operations: data.has_marine_operations || false,
      has_heavy_machinery: data.has_heavy_machinery || false,
      has_recent_wc_claims: data.has_recent_wc_claims || false,
      has_safety_program: data.has_safety_program || false,
      safety_program_details: data.safety_program_details || '',
      
      // Business operation details
      has_heights_work: data.has_heights_work || false,
      heights_work_details: data.heights_work_details || '',
      max_height: data.max_height || '',
      heights_safety_measures: data.heights_safety_measures || '',
      has_below_ground_work: data.has_below_ground_work || false,
      below_ground_work_details: data.below_ground_work_details || '',
      max_depth: data.max_depth || '',
      confined_space_procedures: data.confined_space_procedures || '',
      has_vehicle_exposure: data.has_vehicle_exposure || false,
      vehicle_count: data.vehicle_count || '',
      driver_count: data.driver_count || '',
      vehicle_safety_program: data.vehicle_safety_program || '',
      has_hazardous_materials: data.has_hazardous_materials || false,
      hazardous_materials_details: data.hazardous_materials_details || '',
    },
  });

  useEffect(() => {
    reset(data);
  }, [data, reset]);

  const onFormChange = handleSubmit((formData) => {
    onSave(formData);
  });

  return (
    <form onChange={onFormChange} onSubmit={(e) => e.preventDefault()}>
      <Card>
        <CardContent className="pt-6 space-y-6">
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="business_activities">Business Operations Overview</Label>
              <Textarea
                id="business_activities"
                {...register('business_activities')}
                placeholder="Describe the organization's operations and activities"
                className="min-h-[120px]"
              />
            </div>
            
            <div className="space-y-8">
              <RiskAssessmentQuestions
                register={register}
                watch={watch}
                setValue={setValue}
                onFormChange={onFormChange}
              />
              
              <BusinessOperations
                register={register}
                watch={watch}
                setValue={setValue}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </form>
  );
};

export default OperationalDetails;
